import { Component, Output, EventEmitter, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatTooltipModule } from "@angular/material/tooltip";

/**
 * Compass Controller Component
 * Pure presentational component that displays a compass-style navigation control.
 * All business logic is handled by the parent map-controller component.
 */
@Component({
  selector: "app-compass-controller",
  imports: [CommonModule, MatTooltipModule],
  templateUrl: "./compass-controller.component.html",
  styleUrl: "./compass-controller.component.scss",
  standalone: true,
})
export class CompassControllerComponent {
  /**
   * Rotation angle for the compass ring (controlled by parent)
   */
  @Input() compassRotation: number = 0;

  /**
   * Rotation angle for the compass text labels (controlled by parent)
   */
  @Input() textRotation: number = 0;

  /**
   * Currently selected orientation (N, E, S, W)
   */
  @Input() selectedOrientation: string = "N";

  /**
   * Whether the map is in 3D mode
   */
  @Input() is3DMode: boolean = false;

  /**
   * Tooltip message for the 3D mode button
   */
  @Input() mode3DTooltip: string | null = null;

  /**
   * Whether the 3D button is enabled
   */
  @Input() enable3DButton: boolean = false;

  /**
   * Available orientations (which can be clicked)
   */
  @Input() availableOrientations: Record<string, boolean> = {
    N: true,
    E: true,
    S: true,
    W: true,
  };

  /**
   * Tooltips for orientation buttons when disabled
   */
  @Input() orientationTooltips: Record<string, string> = {};

  /**
   * Event emitted when user toggles between 2D and 3D
   */
  @Output() modeToggle = new EventEmitter<void>();

  /**
   * Event emitted when user selects an orientation
   */
  @Output() orientationChange = new EventEmitter<string>();

  /**
   * Handles orientation button click
   */
  selectOrientation(orientation: string): void {
    if (!this.availableOrientations[orientation]) {
      return;
    }
    this.orientationChange.emit(orientation);
  }

  /**
   * Handles mode toggle button click
   */
  onCenterButtonClick(event: Event): void {
    event.stopPropagation();

    // Allow toggle if in 3D mode OR if 3D is enabled
    if (!this.is3DMode && !this.enable3DButton) {
      return;
    }

    this.modeToggle.emit();
  }

  /**
   * Calculate X position for orientation text (always horizontal)
   * Positions: N=top(0, -38), E=right(38, 0), S=bottom(0, 38), W=left(-38, 0)
   */
  getOrientationX(index: number): number {
    const positions = [0, 38, 0, -38]; // N, E, S, W
    return positions[index];
  }

  /**
   * Calculate Y position for orientation text (always horizontal)
   */
  getOrientationY(index: number): number {
    const positions = [-38, 0, 38, 0]; // N, E, S, W
    return positions[index];
  }
}
